import React, { FC, useCallback, useState } from 'react';
import { IconButton, Table, TableBody, TableHead, TableRow } from '@material-ui/core';
import TableCell from 'src/components/TableCell';
import clsx from 'clsx';
import moment from 'moment';
import Rating from '@material-ui/lab/Rating';
import { IoMegaphoneOutline as TicketIcon } from '@react-icons/all-files/io5/IoMegaphoneOutline';
import { LegacyDeal } from 'src/interfaces/api/Deal';
import { createUseStyles } from 'src/theming';
import { Link } from 'react-router-dom';
import DealHeaderTableCell from './DealHeaderTableCell';
import { sortByDate } from '../utils';

const useStyles = createUseStyles({
  lost: {
    borderLeft: '4px solid #5b4c91',
  },
  nego: {
    borderLeft: '4px solid #f57c73',
  },
  emptyRow: {
    padding: 14,
  },
});

interface Props {
  domainFilter?: number;
  statusFilter?: string;
  showCustomerSatisfaction?: boolean;
  onCreateTicket: (dealId: number) => void;
  deals: LegacyDeal[];
  siteId?: string;
}

const linkToDeal = '/dashboard/deal/';
const linkToUser = '/dashboard/user/';

const DealTable: FC<Props> = ({
  siteId,
  statusFilter,
  deals,
  showCustomerSatisfaction,
  onCreateTicket,
  domainFilter,
}) => {
  const classes = useStyles();
  const [sortBy, setSortBy] = useState<keyof LegacyDeal>('dateCreate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const sortDeals = useCallback(
    (deals: LegacyDeal[]): LegacyDeal[] => {
      return deals.sort((dealA, dealB) => {
        if (['dateCreate', 'contractEndDate'].includes(sortBy)) {
          return sortByDate(dealA[sortBy], dealB[sortBy], sortDirection);
        }

        return 0;
      });
    },
    [sortDirection, sortBy]
  );

  const getFilteredDeals = useCallback((): LegacyDeal[] => {
    let filteredDeals = domainFilter ? deals.filter((deal) => deal.domain?.id === domainFilter) : deals;

    if (showCustomerSatisfaction) {
      filteredDeals = filteredDeals.filter(
        (deal) =>
          deal.customerSatisfactionPreparationRating ||
          deal.customerSatisfactionInstallationRating ||
          deal.customerSatisfactionCommercialRating
      );
    }

    switch (statusFilter) {
      case 'lost':
        return filteredDeals.filter((deal) => deal.lost);
      case 'win':
        return filteredDeals.filter((deal) => dealIsWon(deal));
      case 'negotiation':
        return filteredDeals.filter((deal) => dealIsInNegotiation(deal));
      default:
        return filteredDeals;
    }
  }, [deals, domainFilter, statusFilter, showCustomerSatisfaction]);

  const dealIsInNegotiation = (deal: LegacyDeal) => {
    return !deal.lost && (deal.stepCommercial?.intermediateStageNumber || 0) < 3;
  };

  const dealIsWon = (deal: LegacyDeal) => {
    return !deal.lost && (deal.stepCommercial?.intermediateStageNumber || 0) >= 3;
  };

  const getAverageRating = useCallback((deal: LegacyDeal) => {
    let ratings = 0;
    let nbRatings = 0;

    if (deal.customerSatisfactionCommercialRating) {
      nbRatings++;
      ratings += deal.customerSatisfactionCommercialRating;
    }

    if (deal.customerSatisfactionInstallationRating) {
      nbRatings++;
      ratings += deal.customerSatisfactionInstallationRating;
    }

    if (deal.customerSatisfactionPreparationRating) {
      nbRatings++;
      ratings += deal.customerSatisfactionPreparationRating;
    }

    return nbRatings !== 0 ? ratings / nbRatings : 0;
  }, []);

  const getRatingTitle = useCallback((deal: LegacyDeal) => {
    let title = '';

    if (deal.customerSatisfactionCommercialRating) {
      title += `Commercial: ${deal.customerSatisfactionCommercialRating}/4\n`;
    }

    if (deal.customerSatisfactionInstallationRating) {
      title += `Installation: ${deal.customerSatisfactionInstallationRating}/4\n`;
    }

    if (deal.customerSatisfactionPreparationRating) {
      title += `Préparation: ${deal.customerSatisfactionPreparationRating}/4`;
    }

    return title;
  }, []);

  const handleClickHeaderCell = useCallback(
    (data: keyof LegacyDeal) => () => {
      setSortDirection((direction) => (sortBy === data ? (direction === 'asc' ? 'desc' : 'asc') : 'asc'));
      setSortBy(data);
    },
    [sortBy]
  );

  const filteredDeals = getFilteredDeals();

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Affaire</TableCell>
          <TableCell>Type</TableCell>
          <DealHeaderTableCell
            onClick={handleClickHeaderCell('dateCreate')}
            name='Création'
            sortDirection={sortBy === 'dateCreate' ? sortDirection : undefined}
          />
          <DealHeaderTableCell
            onClick={handleClickHeaderCell('contractEndDate')}
            name="Fin d'engagement"
            sortDirection={sortBy === 'contractEndDate' ? sortDirection : undefined}
          />
          <TableCell>Créateur</TableCell>
          {showCustomerSatisfaction ? (
            <TableCell align='right'>Satisfaction client</TableCell>
          ) : (
            <TableCell>Action</TableCell>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {filteredDeals.length === 0 && (
          <TableCell rowSpan={5} className={classes.emptyRow}>
            Aucune affaire pour ce client.
          </TableCell>
        )}

        {sortDeals(filteredDeals).map(
          (deal) =>
            !deal.deleted && (
              <TableRow
                key={deal.id}
                className={clsx({
                  [classes.lost]: deal.lost,
                  [classes.nego]: dealIsInNegotiation(deal),
                })}
              >
                <TableCell>
                  <Link to={linkToDeal + deal.id}>
                    <span>{deal.name}</span>
                  </Link>
                </TableCell>

                <TableCell>{deal.type?.name}</TableCell>
                <TableCell>{moment(deal.dateCreate).format('DD-MM-YY')}</TableCell>
                <TableCell>{deal.contractEndDate ? moment(deal.contractEndDate).format('DD-MM-YY') : ''}</TableCell>
                <TableCell>
                  {deal.creator ? (
                    <Link to={linkToUser + deal.creator.user.id}>
                      <span>{deal.creator.user.username}</span>
                    </Link>
                  ) : (
                    <span>Non-renseigné</span>
                  )}
                </TableCell>
                {showCustomerSatisfaction ? (
                  <TableCell align='right' title={getRatingTitle(deal)}>
                    <Rating size='small' value={getAverageRating(deal)} max={4} precision={0.1} readOnly />
                  </TableCell>
                ) : (
                  <TableCell>
                    {siteId && (
                      <IconButton onClick={() => onCreateTicket(deal.id)} size='small'>
                        <TicketIcon />
                      </IconButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            )
        )}
      </TableBody>
    </Table>
  );
};

export default DealTable;
